/**
 * InlineRowGrid — the workhorse input mechanic (§21a / §24). A `table.ig` whose
 * bottom row (`tr.newline`) IS the add mechanism: type into it and press Enter
 * to commit the row; a fresh empty entry row immediately drops in beneath. No
 * separate "add row" button. Used by Time/NPT, cost lines, personnel, pipe
 * tally, mud products/volumes, BHA components, formation tops, etc.
 *
 * Column-driven and generic over the row shape. Each column supplies a `render`
 * for committed cells and an `input` renderer for the entry row; a column may
 * be numeric (`.num`, right-aligned tabular) and/or `computed` (server-filled,
 * shown dimmed as "auto"). The grid owns only the entry-row draft + the
 * commit/append/delete plumbing; persistence is the caller's (onCommit).
 *
 * Report-lifecycle aware: when `readOnly` (report closed), the entry row is
 * hidden and existing rows lock (§1 report lifecycle).
 */
import {
  useCallback,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

export interface InlineColumn<Row, Draft> {
  /** Stable key + header label. */
  key: string;
  header: ReactNode;
  /** Right-align + tabular-nums. */
  num?: boolean;
  /** Server-computed column — rendered dimmed ("auto") in the entry row. */
  computed?: boolean;
  /** Fixed column width (px) or CSS width. */
  width?: number | string;
  /** Committed-cell renderer. */
  render: (row: Row) => ReactNode;
  /**
   * Entry-row control renderer. Receives the current draft + a setter that
   * patches the draft. Omit for computed columns (they show "auto").
   */
  input?: (draft: Draft, patch: (p: Partial<Draft>) => void) => ReactNode;
}

export interface InlineRowGridProps<Row, Draft> {
  columns: InlineColumn<Row, Draft>[];
  rows: Row[];
  /** Stable row id for React keys + delete. */
  rowKey: (row: Row) => string;
  /** A fresh empty draft for the entry row. */
  emptyDraft: () => Draft;
  /**
   * Validate a draft into a row (or null to reject the commit — e.g. required
   * field missing). Called on Enter / commit.
   */
  toRow: (draft: Draft) => Row | null;
  /** Commit a validated row. Return handled by the caller (persist/optimistic). */
  onCommit: (row: Row) => void;
  /** Optional delete affordance per row. */
  onDelete?: (row: Row) => void;
  /** Mark a row as carried-over (persists across reports, editable). */
  isCarried?: (row: Row) => boolean;
  /** Data-lineage owner tag per row (admin overlay). */
  rowOwner?: (row: Row) => "m" | "p" | "z" | "u" | undefined;
  /** Report closed → hide entry row + lock. */
  readOnly?: boolean;
  /** aria-label for the grid. */
  ariaLabel?: string;
  /** Placeholder text shown when there are no committed rows. */
  emptyLabel?: string;
}

export function InlineRowGrid<Row, Draft extends object>({
  columns,
  rows,
  rowKey,
  emptyDraft,
  toRow,
  onCommit,
  onDelete,
  isCarried,
  rowOwner,
  readOnly = false,
  ariaLabel,
  emptyLabel,
}: InlineRowGridProps<Row, Draft>) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const patch = useCallback((p: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...p }));
  }, []);

  const commit = useCallback(() => {
    const row = toRow(draft);
    if (!row) return; // invalid — required field missing, keep editing
    onCommit(row);
    setDraft(emptyDraft()); // drop a fresh empty entry row
  }, [draft, toRow, onCommit, emptyDraft]);

  const onEntryKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTableRowElement>) => {
      // Enter commits — unless focus is in a textarea (multi-line narrative).
      if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
        e.preventDefault();
        commit();
      }
    },
    [commit],
  );

  const hasDelete = !!onDelete;

  return (
    <div className={`inputwrap${readOnly ? " report-closed" : ""}`}>
      <table className="ig" aria-label={ariaLabel}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={c.num ? "num" : undefined}
                style={c.width != null ? { width: c.width } : undefined}
              >
                {c.header}
              </th>
            ))}
            {hasDelete ? <th aria-label="Actions" style={{ width: 40 }} /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && emptyLabel ? (
            <tr>
              <td className="dim" colSpan={columns.length + (hasDelete ? 1 : 0)}>
                {emptyLabel}
              </td>
            </tr>
          ) : null}
          {rows.map((row) => {
            const carried = isCarried?.(row);
            const owner = rowOwner?.(row);
            return (
              <tr key={rowKey(row)} className={carried ? "carried" : undefined} data-owner={owner}>
                {columns.map((c) => (
                  <td key={c.key} className={`committed${c.num ? " num tab-nums" : ""}`}>
                    {c.render(row)}
                  </td>
                ))}
                {hasDelete ? (
                  <td>
                    {!readOnly ? (
                      <button
                        type="button"
                        className="row-delete"
                        aria-label="Delete row"
                        onClick={() => onDelete?.(row)}
                      >
                        <span className="ic ic-delete" aria-hidden="true" />
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            );
          })}

          {/* the entry row — the add mechanism; hidden when the report is closed */}
          {!readOnly ? (
            <tr className="newline" onKeyDown={onEntryKeyDown}>
              {columns.map((c) => (
                <td key={c.key} className={c.num ? "num" : undefined}>
                  {c.computed ? (
                    <span className="dim" style={{ fontSize: 12 }}>
                      auto
                    </span>
                  ) : (
                    c.input?.(draft, patch)
                  )}
                </td>
              ))}
              {hasDelete ? (
                <td>
                  <button
                    type="button"
                    className="row-delete"
                    aria-label="Add row"
                    title="Press Enter to add"
                    onClick={commit}
                  >
                    <span className="ic ic-add" aria-hidden="true" />
                  </button>
                </td>
              ) : null}
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
